import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Seo from '../../components/Seo';
import Icon from '../../components/AppIcon';
import { appendTrackingParams } from '../../utils/tracking';
import { Link } from 'react-router-dom';

const TutorialsPage = () => {
  const appUrl = (import.meta.env.VITE_APP_URL || 'https://app.neevii.com').replace(/\/$/, '');
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const tutorials = [
    {
      id: 'intro',
      title: 'Introduction to Neevii',
      description: 'Get started with Neevii - learn the basics of AI-powered accounting.',
      youtubeId: 'mebla0P1jiw',
      thumbnail: '/assets/images/Main_feature_landing_landscape_v002.png',
      duration: '3:45'
    }
  ];

  const handlePlayVideo = (youtubeId) => {
    setIsVideoPlaying(youtubeId);
  };

  return (
    <>
      <Seo
        title="Tutorials - Neevii"
        description="Learn how to use Neevii with our video tutorials and guides."
      />
      <div className="min-h-screen text-foreground relative overflow-hidden">

        <Header />
        <main className="relative pt-24 pb-24 px-6 lg:px-8">

          <div className="max-w-4xl mx-auto text-center space-y-8">
            <span className="inline-flex items-center justify-center px-4 py-2 rounded-full border border-border/60 bg-card/50 text-sm text-muted-foreground">
              Tutorials
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight">Learn Neevii</h1>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Watch our video tutorials to get started with AI-powered accounting.
            </p>

            {/* Featured Video - Centered */}
            <div className="flex justify-center mt-12">
              {tutorials.map((tutorial) => (
                <div
                  key={tutorial.id}
                  className="group bg-card border border-border rounded-xl overflow-hidden hover:border-primary/30 transition-all duration-300 w-full max-w-4xl text-left"
                >
                  {/* Thumbnail / Video */}
                  <div className="relative aspect-video bg-muted">
                    {isVideoPlaying === tutorial.youtubeId ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${tutorial.youtubeId}?autoplay=1&vq=hd1080&hd=1&modestbranding=1&rel=0`}
                        title={tutorial.title}
                        className="absolute inset-0 w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <>
                        <img
                          src={tutorial.thumbnail}
                          alt={tutorial.title}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <button
                          onClick={() => handlePlayVideo(tutorial.youtubeId)}
                          className="absolute inset-0 flex items-center justify-center transition-colors"
                        >
                          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300">
                            <Icon name="Play" size={24} className="text-slate-900 ml-0.5" />
                          </div>
                        </button>
                        <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded bg-black/70 text-white text-sm font-medium">
                          {tutorial.duration}
                        </div>
                      </>
                    )}
                  </div>
                  {/* Info */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                      {tutorial.title}
                    </h3>
                    <p className="text-muted-foreground mt-2">
                      {tutorial.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* More coming soon */}
            <div className="rounded-2xl border border-border/60 bg-card/40 p-6 space-y-3 text-sm text-muted-foreground mt-12">
              <p>More tutorials coming soon. Want a specific workflow covered? Email <a href="mailto:support@neevii.com" className="underline">support@neevii.com</a> and we&apos;ll prioritise the most requested topics.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                className="bg-[#cc9500] text-slate-900 font-semibold shadow-lg hover:bg-[#cc9500]/90 hover:shadow-xl transition-all"
                onClick={() => (window.location.href = appendTrackingParams(appUrl))}
              >
                Start Free Trial
              </Button>
            </div>
          </div>
        </main>

        <footer className="border-t border-border bg-card/30 relative">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-sm text-muted-foreground">
              <div className="text-center md:text-left">
                <span className="font-semibold text-foreground">Neevii</span>
                <p className="mt-1">AI accounting for early-stage teams.</p>
              </div>
              <div className="flex items-center gap-4">
                <Link to="/subscription" className="hover:text-foreground transition-colors">Pricing</Link>
                <Link to="/faq" className="hover:text-foreground transition-colors">FAQ</Link>
                <Link to="/facts-for-ai" className="hover:text-foreground transition-colors">Facts for AI</Link>
                <a href={appendTrackingParams(appUrl)} className="hover:text-foreground transition-colors">Start Free Trial</a>
                <a href="mailto:support@neevii.com" className="hover:text-foreground transition-colors">Contact</a>
              </div>
              <div className="text-center md:text-right">
                © {new Date()?.getFullYear()} Neevii. All rights reserved.
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TutorialsPage;